import React, { useEffect, useMemo, useState } from 'react';
import { projectFirestore, timestamp } from '../firebase/config';

/* ── helpers ── */
function toMillis(v) {
  if (!v) return 0;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  const p = new Date(v).getTime();
  return Number.isNaN(p) ? 0 : p;
}

function formatDate(v) {
  const ms = toMillis(v);
  if (!ms) return '';
  return new Date(ms).toLocaleDateString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric' });
}

function drawWrappedText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = (text || '').split(' ');
  let line = '';
  const lines = [];
  for (let i = 0; i < words.length; i++) {
    const next = line ? `${line} ${words[i]}` : words[i];
    if (ctx.measureText(next).width > maxWidth && line) { lines.push(line); line = words[i]; }
    else line = next;
  }
  if (line) lines.push(line);
  lines.slice(0, 8).forEach((l, idx) => ctx.fillText(l, x, y + idx * lineHeight));
}

function createFeedbackImageBlob(item) {
  const canvas = document.createElement('canvas');
  canvas.width = 1200; canvas.height = 1200;
  const ctx = canvas.getContext('2d');
  const g = ctx.createLinearGradient(0, 0, 1200, 1200);
  g.addColorStop(0, '#0f62fe'); g.addColorStop(1, '#0043ce');
  ctx.fillStyle = g; ctx.fillRect(0, 0, 1200, 1200);
  ctx.fillStyle = '#fff'; ctx.textAlign = 'right'; ctx.direction = 'rtl';
  ctx.font = '700 58px Tajawal, sans-serif';
  ctx.fillText('تعليق من زوار حكاية ضوء', 1120, 120);
  ctx.font = '700 48px Tajawal, sans-serif';
  ctx.fillText('★'.repeat(Number(item.rating) || 0) + ` (${Number(item.rating) || 0}/5)`, 1120, 220);
  ctx.font = '500 44px Tajawal, sans-serif';
  drawWrappedText(ctx, item.message, 1120, 320, 960, 70);
  ctx.font = '600 36px Tajawal, sans-serif';
  ctx.fillText(`- ${item.name || 'زائر'}`, 1120, 980);
  ctx.globalAlpha = 0.9; ctx.font = '500 30px Tajawal, sans-serif';
  ctx.fillText('reactgallery-2021.web.app', 1120, 1060);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

/* ── star rating row ── */
function StarRow({ value, onChange, size = 28 }) {
  return (
    <div className="d-flex gap-1 justify-content-center" role="radiogroup" aria-label="التقييم" style={{ direction: 'ltr' }}>
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`fa-star ${value >= s ? 'fas' : 'far'}`}
          style={{ fontSize: size, color: value >= s ? '#f59f00' : '#ccc', cursor: onChange ? 'pointer' : 'default' }}
          onClick={() => onChange && onChange(s)}
          role={onChange ? 'button' : undefined}
          aria-label={`${s} نجوم`}
        />
      ))}
    </div>
  );
}

/* ── component ── */
const GlobalFeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('comments'); // 'comments' | 'add'
  const [name, setName] = useState('');
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [shareNotice, setShareNotice] = useState('');
  const [sharingId, setSharingId] = useState('');
  const [published, setPublished] = useState([]);

  useEffect(() => {
    const unsub = projectFirestore.collection('siteFeedback')
      .where('status', '==', 'published')
      .onSnapshot((snap) => {
        const list = [];
        snap.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
        list.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        setPublished(list);
      });
    return () => unsub();
  }, []);

  const avg = useMemo(() => {
    if (!published.length) return 0;
    return published.reduce((s, i) => s + (Number(i.rating) || 0), 0) / published.length;
  }, [published]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const txt = comment.trim();
    if (txt.length < 5) { setError('يرجى كتابة رأي واضح (5 أحرف على الأقل).'); return; }
    setSaving(true); setError(''); setNotice('');
    try {
      await projectFirestore.collection('siteFeedback').add({
        name: name.trim() || 'زائر',
        rating: Number(rating) || 5,
        message: txt, comment: txt,
        status: 'pending',
        visitorId: localStorage.getItem('spe_visitor_id') || null,
        userAgent: navigator.userAgent,
        createdAt: timestamp(), updatedAt: timestamp(),
      });
      setName(''); setRating(5); setComment('');
      setNotice('شكراً لك! تم إرسال رأيك وسيظهر بعد مراجعته.');
    } catch { setError('تعذر الإرسال حالياً، حاول مرة أخرى.'); }
    setSaving(false);
  };

  const handleShare = async (item) => {
    setShareNotice(''); setSharingId(item.id);
    try {
      const blob = await createFeedbackImageBlob({ ...item, message: item.comment || item.message || '' });
      if (!blob) throw new Error();
      const file = new File([blob], `feedback-${item.id}.png`, { type: 'image/png' });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'تعليق مميز', text: 'رأي من موقع حكاية ضوء' });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = file.name;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        setShareNotice('تم تنزيل الصورة.');
      }
    } catch { setShareNotice('تعذرت المشاركة حالياً.'); }
    setSharingId('');
  };

  return (
    <>
      {/* ── floating star icon ── */}
      <button
        className="feedback-fab-btn"
        onClick={() => { setOpen(true); setTab('comments'); }}
        aria-label="التقييمات والآراء"
        title="التقييمات والآراء"
      >
        <i className="fas fa-star" />
        {published.length > 0 && <span className="feedback-fab-badge">{avg.toFixed(1)}</span>}
      </button>

      {/* ── modal ── */}
      {open && (
        <div className="feedback-overlay" onClick={() => setOpen(false)}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>

            {/* header */}
            <div className="feedback-modal-header">
              <h5 className="mb-0"><i className="fas fa-star text-warning me-2" />التقييمات والآراء</h5>
              <button className="btn-close" onClick={() => setOpen(false)} aria-label="إغلاق" />
            </div>

            {/* summary bar */}
            <div className="feedback-summary">
              <StarRow value={Math.round(avg)} size={22} />
              <span className="fw-bold mx-2">{avg.toFixed(1)}</span>
              <span className="text-muted">({published.length} تقييم)</span>
            </div>

            {/* tabs */}
            <div className="feedback-tabs">
              <button className={`feedback-tab ${tab === 'comments' ? 'active' : ''}`} onClick={() => setTab('comments')}>
                <i className="fas fa-comments me-1" />الآراء المنشورة
              </button>
              <button className={`feedback-tab ${tab === 'add' ? 'active' : ''}`} onClick={() => setTab('add')}>
                <i className="fas fa-plus me-1" />أضف رأيك
              </button>
            </div>

            {/* tab content */}
            <div className="feedback-body">
              {tab === 'comments' && (
                <>
                  {shareNotice && <div className="alert alert-info py-2 small">{shareNotice}</div>}
                  {published.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="far fa-comment-dots" style={{ fontSize: 48, opacity: 0.3 }} />
                      <p className="mt-3 mb-0">لا توجد آراء منشورة بعد</p>
                    </div>
                  ) : (
                    <div className="feedback-cards">
                      {published.map((item) => (
                        <div key={item.id} className="feedback-card">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <div className="feedback-avatar">{(item.name || 'ز')[0]}</div>
                              <div>
                                <div className="fw-bold" style={{ fontSize: 14 }}>{item.name || 'زائر'}</div>
                                <small className="text-muted">{formatDate(item.createdAt)}</small>
                              </div>
                            </div>
                            <StarRow value={Number(item.rating) || 0} size={14} />
                          </div>
                          <p className="mb-2" style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.7 }}>{item.comment || item.message}</p>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => handleShare(item)} disabled={sharingId === item.id}>
                            <i className="fas fa-share-alt me-1" />{sharingId === item.id ? 'جارٍ التجهيز...' : 'مشاركة كصورة'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {tab === 'add' && (
                <form onSubmit={handleSubmit} className="feedback-form">
                  <div className="text-center mb-3">
                    <div className="text-muted small mb-2">ما تقييمك للموقع؟</div>
                    <StarRow value={rating} onChange={setRating} size={36} />
                  </div>
                  <div className="mb-3">
                    <input type="text" className="form-control" value={name} onChange={(e) => setName(e.target.value)} placeholder="اسمك (اختياري)" />
                  </div>
                  <div className="mb-3">
                    <textarea className="form-control" rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="اكتب رأيك هنا..." style={{ resize: 'none' }} />
                  </div>
                  {notice && <div className="alert alert-success py-2 small">{notice}</div>}
                  {error && <div className="alert alert-danger py-2 small">{error}</div>}
                  <button type="submit" className="btn btn-warning w-100 fw-bold" disabled={saving}>
                    <i className="fas fa-paper-plane me-1" />{saving ? 'جارٍ الإرسال...' : 'إرسال التقييم'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalFeedbackWidget;
